import * as React from "react";
import { useCallback } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/lib/shadcn/ui/button";
import { Checkbox } from "@/lib/shadcn/ui/checkbox";
import { Label } from "@/lib/shadcn/ui/label";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { useTriggerHostHubSyncMutation } from "@/store/hosthub-api";

interface SyncControlBarProps {
    /**
     * "Hide cancelled" toggle state (default OFF → cancelled shown; FR-014,
     * hosthub-reservation-ingestion). Lifted so the grid can filter client-side.
     */
    hideCancelled: boolean;
    onHideCancelledChange: (next: boolean) => void;
    /** Fired after a successful sync so the caller can refetch the grid. */
    onSynced?: () => void;
    /**
     * Render the "Hide cancelled" filter. Defaults to true; pages with no
     * cancelled rows to filter pass false rather than show a control that does
     * nothing (hosthub-event-durability/FR-018).
     */
    showCancelledFilter?: boolean;
}

/**
 * SyncControlBar (ui-spec Screen 5) — a small toolbar above the reservations
 * grid: a "Sync now" button that triggers the same logic as the hourly HostHub
 * job, a polite status line (announced to assistive tech via aria-live), and
 * the "Hide cancelled" client-side filter toggle.
 *
 * Success → polite toast "Synced: N new · M updated · K cancelled".
 * Error → destructive toast with the failure reason.
 */
export const SyncControlBar: React.FC<SyncControlBarProps> = ({
    hideCancelled,
    onHideCancelledChange,
    onSynced,
    showCancelledFilter = true,
}) => {
    const [triggerSync, { isLoading }] = useTriggerHostHubSyncMutation();
    // The live region stays empty on load — there is no last-sync data source to
    // report, and a static "Never synced" placeholder was misleading (it implied
    // the account had never synced when it only meant "not synced from this tab
    // this session"). It populates with progress/result text once a sync runs.
    const [status, setStatus] = React.useState<string>("");

    const handleSync = useCallback(async () => {
        setStatus("Syncing…");
        try {
            const summary = await triggerSync().unwrap();
            const message = `Synced: ${summary.created} new · ${summary.modified} updated · ${summary.cancelled} cancelled`;
            setStatus(message);
            toast({ title: "Sync complete", description: message });
            onSynced?.();
        } catch (err) {
            const reason =
                typeof err === "object" && err !== null && "data" in err
                    ? extractReason((err as { data: unknown }).data)
                    : "Unknown error";
            setStatus(`Sync failed — ${reason}`);
            toast({
                title: "Sync failed",
                description: reason,
                variant: "destructive",
            });
        }
    }, [triggerSync, onSynced]);

    return (
        <div className="flex flex-wrap items-center gap-3" data-testid="sync-control-bar">
            <Button
                type="button"
                data-testid="sync-now-button"
                onClick={handleSync}
                disabled={isLoading}
                aria-label={isLoading ? "Sync now, syncing in progress" : "Sync now"}
                className="gap-2"
            >
                {isLoading ? (
                    <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
                ) : (
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                )}
                {isLoading ? "Syncing…" : "Sync now"}
            </Button>

            {showCancelledFilter && (
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="hide-cancelled-toggle"
                        data-testid="hide-cancelled-toggle"
                        checked={hideCancelled}
                        onCheckedChange={(checked) => onHideCancelledChange(checked === true)}
                    />
                    <Label htmlFor="hide-cancelled-toggle" className="text-sm font-normal">
                        Hide cancelled
                    </Label>
                </div>
            )}

            <p data-testid="sync-status" aria-live="polite" className="text-sm text-slate-600">
                {status}
            </p>
        </div>
    );
};

/**
 * Pull a human-readable reason out of the standard error envelope
 * (`{ errors: [{ message }] }`) returned by the API, falling back gracefully.
 */
function extractReason(data: unknown): string {
    if (
        typeof data === "object" &&
        data !== null &&
        "errors" in data &&
        Array.isArray((data as { errors: unknown }).errors)
    ) {
        const errors = (data as { errors: { message?: string }[] }).errors;
        const messages = errors.map((e) => e.message).filter(Boolean);
        if (messages.length > 0) {
            return messages.join(", ");
        }
    }
    return "Unknown error";
}

export default SyncControlBar;
